package org.teddapp.project.core

import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/**
 * Ce que fait le noyau, en clair. Un écran de diagnostic sans détour : la bibliothèque
 * est-elle chargée, combien de faits sont écrits, ce que la projection en a tiré, et
 * ce que l'agent recommande maintenant.
 *
 * C'est ici qu'on vérifie que le Rust travaille vraiment, sans lire un fichier à la main.
 */
@Composable
fun CoreDiagnosticsPanel(modifier: Modifier = Modifier) {
    val session = LocalCoreSession.current
    val bridge = session?.bridge
    var events by remember { mutableStateOf(0L) }
    var applied by remember { mutableStateOf(0L) }
    var snapshot by remember { mutableStateOf("") }
    var lastEvents by remember { mutableStateOf<List<String>>(emptyList()) }
    val lastSync = session?.lastSync?.collectAsState()?.value
    var learned by remember { mutableStateOf(0L) }
    var coreVersion by remember { mutableStateOf("") }
    var refresh by remember { mutableStateOf(0) }

    LaunchedEffect(bridge, refresh) {
        val core = bridge ?: return@LaunchedEffect
        withContext(Dispatchers.Default) {
            runCatching {
                applied = core.catchUp()
                events = core.eventCount()
                snapshot = core.snapshotJson()
                lastEvents = core.eventsAfter(maxOf(0L, events - 5))
                learned = core.agentKnownStates()
                coreVersion = core.coreVersion()
            }
        }
    }

    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Box(
                modifier = Modifier
                    .size(9.dp)
                    .clip(CircleShape)
                    .background(if (bridge != null) TeddTokens.Success else TeddTokens.InkSubtle, CircleShape),
            )
            Text(
                text = when {
                    bridge == null -> "Noyau absent, mode local"
                    coreVersion.isBlank() -> "Noyau Rust chargé"
                    // Deux numéros qui divergent expliquent la plupart des
                    // comportements qu'on ne s'explique pas.
                    coreVersion == org.teddapp.project.TeddVersion.NAME -> "Noyau Rust $coreVersion"
                    else -> "Noyau $coreVersion · application ${org.teddapp.project.TeddVersion.NAME}"
                },
                style = TeddType.Heading,
                color = TeddTokens.Ink,
                modifier = Modifier.weight(1f),
            )
            if (bridge != null) {
                TeddButton(
                    text = "Rafraîchir",
                    onClick = { refresh += 1 },
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Sm,
                )
            }
        }

        if (bridge == null) {
            Text(
                text = "La bibliothèque native n'a pas été trouvée. Tout fonctionne en local : " +
                    "aucune donnée n'est perdue, seuls le fil des réactions et la recommandation " +
                    "de l'agent restent silencieux.",
                style = TeddType.BodyLg,
                color = TeddTokens.InkMuted,
            )
            return@Column
        }

        Row(horizontalArrangement = Arrangement.spacedBy(26.dp)) {
            CoreNumber("faits écrits", events.toString())
            CoreNumber("rejoués", applied.toString())
            CoreNumber("publiés", lastSync?.pushedLocalOffset?.toString() ?: "—")
            CoreNumber("reçus", lastSync?.lastGlobalOffset?.toString() ?: "—")
            CoreNumber("situations apprises", learned.toString())
            CoreNumber("réactions", session.reactions.value.size.toString())
        }

        session.recommendation.value?.let { reco ->
            TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(14.dp)) {
                Text("CE QUE L'AGENT RECOMMANDE", style = TeddType.Overline, color = TeddTokens.InkMuted)
                Text(reco.label, style = TeddType.BodyLg, color = TeddTokens.Ink, modifier = Modifier.padding(top = 4.dp))
                Text("${reco.action} · ${reco.minutes} min", style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 12.sp)
            }
        }

        if (snapshot.isNotBlank()) {
            CoreJsonCard("INSTANTANÉ DE PROJECTION", snapshot)
        }
        if (lastEvents.isNotEmpty()) {
            Text("DERNIERS FAITS", style = TeddType.Overline, color = TeddTokens.InkMuted)
            lastEvents.takeLast(5).reversed().forEach { CoreJsonCard(null, it) }
        } else {
            Text(
                text = "Aucun fait pour l'instant. Lance une course de focus, observe une envie, " +
                    "ou crée un objectif : chaque geste écrit une ligne ici.",
                style = TeddType.BodyLg,
                color = TeddTokens.InkMuted,
            )
        }
    }
}

@Composable
private fun CoreNumber(label: String, value: String) {
    Column {
        Text(value, style = TeddType.Numeral.copy(fontSize = 22.sp, lineHeight = 26.sp), color = TeddTokens.Ink)
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted)
    }
}

/** Un bloc de JSON brut, lisible et défilable, sans mise en forme trompeuse. */
@Composable
private fun CoreJsonCard(title: String?, json: String) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(12.dp))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        if (title != null) Text(title, style = TeddType.Overline, color = TeddTokens.InkMuted)
        Box(modifier = Modifier.fillMaxWidth().heightIn(max = 120.dp).horizontalScroll(rememberScrollState())) {
            Text(json, style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 11.5.sp)
        }
    }
}
